/**
 * AI导演工作台页面
 * 实现从灵感到长视频的自动化工作流
 */

import { Link } from 'react-router-dom'
import { Home, Shield, X } from 'lucide-react'
import { useDirectorState } from '../hooks/useDirectorState'
import { CardGrid } from '../components/director/CardGrid'
import { DirectorConsole } from '../components/director/DirectorConsole'
import { StoryboardTimeline } from '../components/director/StoryboardTimeline'

export function DirectorPage() {
  const state = useDirectorState()

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">🎬 导演工作台</h1>
          <div className="flex gap-2">
            <Link
              to="/"
              className="flex items-center px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm"
            >
              <Home className="w-3.5 h-3.5 mr-1.5" />
              回到首页
            </Link>
            <Link
              to="/admin"
              className="flex items-center px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm"
            >
              <Shield className="w-3.5 h-3.5 mr-1.5" />
              管理界面
            </Link>
          </div>
        </div>

        {/* ModuleA: 灵感起爆区 */}
        <div className="bg-gray-800 rounded-lg p-3 mb-4 border border-gray-700">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-base font-semibold">✨ 第一步：描述你的画面灵感</h2>
            <button
              onClick={state.handleNewCreation}
              className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-xs font-medium"
            >
              重新开始
            </button>
          </div>
          <div className="space-y-2">
            <div>
              <label className="block text-xs font-medium mb-1">画面灵感 *</label>
              <textarea
                value={state.seedPrompt}
                onChange={(e) => state.setSeedPrompt(e.target.value)}
                placeholder="用几个词描述你想要的画面，例如：星球大战、雨中的东京街头、水墨风山水"
                className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                rows={2}
                disabled={state.isGeneratingT2I}
              />
            </div>
            <div className="grid grid-cols-[1fr,1fr,auto] gap-2">
              <div>
                <label className="block text-xs font-medium mb-1">风格偏好（可选）</label>
                <input
                  type="text"
                  value={state.stylePreference}
                  onChange={(e) => state.setStylePreference(e.target.value)}
                  placeholder="例如：赛博朋克、电影质感、水彩风"
                  className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                  disabled={state.isGeneratingT2I}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">生成数量</label>
                <select
                  value={state.imageCount}
                  onChange={(e) => state.setImageCount(Number(e.target.value))}
                  className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                  disabled={state.isGeneratingT2I}
                >
                  <option value={1}>1</option>
                  <option value={3}>3</option>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={state.handleGenerateT2I}
                  disabled={state.isGeneratingT2I || !state.seedPrompt.trim()}
                  className="h-[calc(0.875rem+0.25rem+1.875rem)] px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors whitespace-nowrap flex items-center text-sm"
                >
                  {state.isGeneratingT2I ? '⏳ 正在生成画面方案...' : '✨ 生成画面方案'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ModuleB: 十连抽选卡区 */}
        {state.cards.length > 0 && (
          <CardGrid
            cards={state.cards}
            selectedCardId={state.selectedCardId}
            onSelectCard={state.setSelectedCardId}
            onGeneratePreview={state.handleGeneratePreview}
            previewImages={state.previewImages}
            batchPreviewMode={state.batchPreviewMode}
            batchGenerating={state.batchGenerating}
            isGeneratingT2I={state.isGeneratingT2I}
            onBatchPreview={state.handleBatchPreview}
            onOpenPreviewModal={state.handleOpenPreviewModal}
            variantTargetCardId={state.variantTargetCardId}
            setVariantTargetCardId={state.setVariantTargetCardId}
            variantModification={state.variantModification}
            setVariantModification={state.setVariantModification}
            isGeneratingVariant={state.isGeneratingVariant}
            onGenerateVariant={state.handleGenerateVariant}
          />
        )}

        {/* ModuleC: 导演控制台 */}
        {state.selectedCard && (
          <DirectorConsole
            selectedCard={state.selectedCard}
            videoRequirement={state.videoRequirement}
            setVideoRequirement={state.setVideoRequirement}
            durationMode={state.durationMode}
            setDurationMode={state.setDurationMode}
            isGeneratingI2V={state.isGeneratingI2V}
            onGenerateI2V={state.handleGenerateI2V}
            cameraMovements={state.cameraMovements}
            setCameraMovements={state.setCameraMovements}
            rhythm={state.rhythm}
            setRhythm={state.setRhythm}
          />
        )}

        {/* ModuleD: 分镜剧本时间轴 */}
        {state.currentI2vResult && (
          <StoryboardTimeline
            i2vResult={state.currentI2vResult}
            videoRefineInput={state.videoRefineInput}
            setVideoRefineInput={state.setVideoRefineInput}
            isRefiningVideo={state.isRefiningVideo}
            onRefineVideoPrompt={state.handleRefineVideoPrompt}
          />
        )}
      </div>

      {/* 预览图片 Modal */}
      {state.previewModalImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => state.setPreviewModalImage(null)}
        >
          <button
            onClick={() => state.setPreviewModalImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="relative max-w-5xl max-h-[90vh] flex flex-col">
            <img
              src={state.previewModalImage.url}
              alt={state.previewModalImage.prompt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 p-4 bg-gray-800/90 rounded-lg max-w-3xl mx-auto">
              <p className="text-sm text-gray-300">{state.previewModalImage.prompt}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
